import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useSearchParams } from 'react-router-dom';
import CustomAlert from '../CustomAlert';
import QRCodeGenerator from '../QRCodeGenerator';
import { handlePrintNote } from '../../utils/printDeliveryNote';
import { t } from '../../i18n';

const apiUrl = process.env.REACT_APP_API_URL;

const InvoiceGenerator = forwardRef(({ hideSidebar = false, existingInvoiceData = null, isEdit = false }, ref) => {
  const [searchParams] = useSearchParams();
  const dnoteId = searchParams.get('dnote_id');
  const [savedInvoiceId, setSavedInvoiceId] = useState(null); // Track ID for new invoices
  
  const [invoice, setInvoice] = useState({
    logo: 'https://elkos.mk/img/custom/logo_green.png', // Replace with your logo URL
    invoiceNumber: '001', // Will be updated with suggested number
    date: new Date().toISOString().slice(0, 10),
    partner_id: null,
    recipient: '',
    recipientAddress: '',
    recipientBankNo: '',
    dnote_number: '',
    items: [{ id: 1, name: '', quantity: 1, price: '', taxRate: 18 }],
    reklamaciiText: 'Робата е предадена во исправна состојба. Купувачот е должен да ја провери при приемот. Со потпишување на испратницата, купувачот ја потврдува исправноста и количината на примена роба. Сите евентуални рекламации се прифаќаат само при прием.',
    currency: 'ден',
    status: 'complete' // Default status
  });

  // View mode state - default to advanced
  const [viewMode, setViewMode] = useState('advanced');

  // Business details state
  const [businessDetails, setBusinessDetails] = useState({
    website: '',
    address: '',
    phone: '',
    email: '',
    bank_account: '',
    bank_name: '',
    full_name: 'ЕЛКОС ДООЕЛ Скопје',
    city: '',
    tax_id: '',
    currency: 'ден'
  });

  // Function to fetch business details
  const fetchBusinessDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${apiUrl}/business-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBusinessDetails({
          website: data.website || '',
          address: data.address || '',
          phone: data.phone || '',
          email: data.email || '',
          bank_account: data.bank_account || '',
          full_name: data.full_name || '',
          city: data.city || '',
          tax_id: data.tax_id || '',
          bank_name: data.bank_name || '',
          currency: data.currency || 'ден'
        });
        
        // Update invoice currency when business details are loaded
        setInvoice(prev => ({
          ...prev,
          currency: data.currency || 'ден'
        }));
      }
    } catch (error) {
      console.error('Failed to fetch business details:', error);
    }
  };

  // Function to fetch next suggested invoice number
  const fetchNextInvoiceNumber = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return; // Don't fetch if not logged in

      const response = await fetch(`${apiUrl}/invoice-next-number`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setInvoice(prev => ({
          ...prev,
          invoiceNumber: data.nextNumber
        }));
      }
    } catch (error) {
      console.error('Failed to fetch next invoice number:', error);
      // Keep default value on error
    }
  };

  // Function to fetch delivery note data when creating from delivery note
  const fetchDeliveryNoteData = async (dnoteId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !dnoteId) return;

      const response = await fetch(`${apiUrl}/dnote-view/${dnoteId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const deliveryNote = data.deliveryNote;
        const items = data.items || [];

        // Update invoice with delivery note data
        setInvoice(prev => ({
          ...prev,
          partner_id: deliveryNote?.partner_id || null,
          recipient: deliveryNote?.partner_name || '',
          recipientAddress: deliveryNote?.partner_address || '',
          recipientBankNo: deliveryNote?.partner_phone || '', // Assuming phone is available
          dnote_number: deliveryNote?.dnote_number || '',
          items: items.length > 0 
            ? items.map((item, index) => ({
                id: index + 1,
                name: item.product_name || '',
                quantity: item.quantity || 1,
                price: item.product_price || '',
                taxRate: item.product_tax || 18,
                product_id: item.product_id || null
              }))
            : prev.items
        }));

        // Show success alert
        showAlert('info', t('Фактура креирана од испратница'), 
          t('Фактурата е пополнета со податоци од испратница #{number}', { number: deliveryNote?.dnote_number || dnoteId }),
          t('Партнер: {partner} • Ставки: {count}', { partner: deliveryNote?.partner_name || t('Н/П'), count: items.length || 0 })
        );
      }
    } catch (error) {
      console.error('Failed to fetch delivery note data:', error);
      showAlert('error', t('Грешка при вчитување на испратницата'), 
        t('Не може да се вчитаат податоците за испратницата. Ве молиме обидете се повторно.'),
        error.message
      );
    }
  };

  // Fetch next invoice number and business details when component mounts
  useEffect(() => {
    if (!isEdit) {
      fetchNextInvoiceNumber();
    }
    fetchBusinessDetails();
    
    // If created from delivery note (dnote_id in URL params), fetch delivery note data
    if (dnoteId && !isEdit) {
      fetchDeliveryNoteData(dnoteId);
    }
  }, [dnoteId, isEdit]);

  // Load existing invoice data for edit mode
  useEffect(() => {
    if (isEdit && existingInvoiceData) {
      const { invoice: invoiceData, items } = existingInvoiceData;
      
      // Transform the items to match the expected format
      const transformedItems = items.map((item, index) => ({
        id: index + 1,
        product_id: item.product_id,
        name: item.name || item.product_name,
        quantity: item.quantity,
        price: item.price,
        taxRate: item.tax_rate || 18
      }));

      // Update invoice state with existing data
      setInvoice(prev => ({
        ...prev,
        invoiceNumber: invoiceData.invoice_number || prev.invoiceNumber,
        date: invoiceData.date ? invoiceData.date.split('T')[0] : prev.date,
        partner_id: invoiceData.partner_id,
        recipient: invoiceData.customer_name || '',
        recipientAddress: invoiceData.address || '',
        items: transformedItems.length > 0 ? transformedItems : prev.items,
        status: invoiceData.status || 'complete'
      }));
    }
  }, [isEdit, existingInvoiceData]);

  // Custom Alert State
  const [alert, setAlert] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    details: ''
  });

  const showAlert = (type, title, message, details = '') => {
    setAlert({
      isOpen: true,
      type,
      title,
      message,
      details
    });
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, isOpen: false }));
  };

  // Expose functions to parent component
  useImperativeHandle(ref, () => ({
    handlePrint: handlePrintClick,
    handleSave,
    handleSaveDraft,
    handleAddRow,
    toggleReklamacije,
    toggleFooter,
    toggleIspratnicaEnabled,
    togglePodignalEnabled,
    toggleVoziloEnabled,
    setViewMode,
    // Expose state values
    reklamacijeEnabled,
    footerEnabled,
    IspratnicaEnabled,
    PodignalEnabled,
    voziloEnabled,
    viewMode
  }));

  // const discount = 0; // Unused for now
  
  // Calculate per item subtotal, tax amount, and total - unused for now
  // const itemsWithCalculations = invoice.items.map(item => {
  //   if (!item.quantity || !item.price) return item;
  //   const quantity = parseFloat(item.quantity);
  //   const price = parseFloat(item.price);
  //   const taxRate = parseFloat(item.taxRate || 18) / 100;
  //   
  //   const itemSubtotal = quantity * price;
  //   const itemTaxAmount = itemSubtotal * taxRate;
  //   const itemTotal = itemSubtotal + itemTaxAmount;
  //   
  //   return {
  //     ...item,
  //     calculatedSubtotal: itemSubtotal,
  //     calculatedTaxAmount: itemTaxAmount,
  //     calculatedTotal: itemTotal
  //   };
  // });
  
  // Process items to include calculated values
  const processedItems = invoice.items.map(item => {
    const quantity = parseFloat(item.quantity || 0);
    const price = parseFloat(item.price || 0);
    const taxRate = parseFloat(item.taxRate || 18); // Default to 18% if not specified
    const lineTotal = quantity * price;
    const taxAmount = lineTotal * (taxRate / 100);
    
    return {
      ...item,
      lineTotal,
      taxAmount
    };
  });

  // Calculate subtotal, tax, and total
  const subtotal = processedItems.reduce((sum, item) => sum + item.lineTotal, 0);
  const tax = processedItems.reduce((sum, item) => sum + item.taxAmount, 0);
  const total = subtotal + tax;  const handleProductItemChange = (index, field, value) => {
    const updatedItems = [...invoice.items];
    if (field === 'quantity') {
      updatedItems[index][field] = parseInt(value) || 1;
    } else if (field === 'price') {
      updatedItems[index][field] = parseFloat(value);
    } else {
      updatedItems[index][field] = value;
    }
    setInvoice({ ...invoice, items: updatedItems });
  };

  const handlePrintClick = () => {
    // For print action, we don't upload to server since PDFs are already uploaded during save
    // This prevents duplicate uploads
    const invoiceId = isEdit ? existingInvoiceData?.invoice?.id : null;
    console.log('Print click - isEdit:', isEdit, 'invoiceId:', invoiceId);
    handlePrintNote(invoice, showAlert, closeAlert, 'invoice', false, invoiceId);
  };

  const handleSaveDraft = async () => {
    // Save as draft
    await handleSave('draft');
  };

  // Function to reset the invoice form to initial state
  const resetInvoiceForm = async () => {
    // Reset form first
    setInvoice({
      logo: 'https://elkos.mk/img/custom/logo_green.png',
      invoiceNumber: '001', // Temporary, will be updated by fetchNextInvoiceNumber
      date: new Date().toISOString().slice(0, 10), // Set to current date
      partner_id: null, // Reset partner ID
      recipient: '',
      recipientAddress: '',
      recipientBankNo: '',
      dnote_number: '',
      items: [{ id: Date.now(), name: '', quantity: 1, price: '', taxRate: 18 }],
      note: '',
      currency: businessDetails.currency || 'ден',
      paymentDueInDays: 7,
      status: 'complete' // Reset to default status
    });
    
    // Clear any partner suggestions
    setSuggestionsPartner([]);
    setShowPartnerSuggestions(false);
    setQueryPartner('');
    
    // Clear any product suggestions
    setSuggestionsProduct([]);
    setProductActiveRow(null);
    
    // Fetch the next suggested invoice number from database
    await fetchNextInvoiceNumber();
  };

  const [validationErrors, setValidationErrors] = useState({});

  const validateForm = () => {
    const errors = {};
    
    if (!invoice.invoiceNumber?.trim()) {
      errors.invoiceNumber = t('Бројот на фактурата е задолжителен');
    }
    
    if (!invoice.partner_id) {
      errors.recipient = t('Ве молиме изберете партнер од листата');
    }
    
    if (!invoice.date) {
      errors.date = t('Датумот на фактурата е задолжителен');
    }
    
    // Validate items - check for empty rows
    const hasEmptyRows = invoice.items.some(item => {
      const hasAnyData = item.name?.trim() || item.quantity || item.price;
      const isComplete = item.name?.trim() && 
                        item.quantity && 
                        item.price && 
                        parseFloat(item.quantity) > 0 && 
                        parseFloat(item.price) > 0;
      
      // If row has some data but is not complete, it's an empty/incomplete row
      return hasAnyData && !isComplete;
    });
    
    const validItems = invoice.items.filter(item => 
      item.name?.trim() && 
      item.quantity && 
      item.price && 
      parseFloat(item.quantity) > 0 && 
      parseFloat(item.price) > 0
    );
    
    if (validItems.length === 0) {
      errors.items = t('Потребна е барем една валидна ставка со назив, количина и цена');
    } else if (hasEmptyRows) {
      errors.items = t('Ве молиме пополнете ги сите ставки или отстранете ги празните редови пред зачувување');
    }
    
    return errors;
  };

  const handleSave = async (saveAsStatus = 'complete') => {
    // Clear previous validation errors
    setValidationErrors({});
    
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      
      // Show detailed validation message
      const errorMessages = Object.values(errors).join('\n• ');
      showAlert('error', t('Грешка при валидација'), t('Ве молиме поправете ги следниве грешки:'), `• ${errorMessages}`);
      return;
    }

    try {
      // Check for incomplete rows before processing
      const hasIncompleteRows = invoice.items.some(item => {
        const hasAnyData = item.name?.trim() || item.quantity || item.price;
        const isComplete = item.name?.trim() && 
                          item.quantity && 
                          item.price && 
                          parseFloat(item.quantity) > 0 && 
                          parseFloat(item.price) > 0;
        
        return hasAnyData && !isComplete;
      });

      if (hasIncompleteRows) {
        showAlert('error', t('Непополнети ставки'), t('Ве молиме пополнете ги сите ставки или отстранете ги празните редови пред зачувување.'));
        return;
      }

      const validItems = invoice.items.filter(item => 
        item.name?.trim() && 
        item.quantity && 
        item.price && 
        parseFloat(item.quantity) > 0 && 
        parseFloat(item.price) > 0
      );

      // Sanitize data to ensure no circular references
      const sanitizedItems = validItems.map(item => {
        // Only extract primitive values, avoid any object references
        return {
          product_id: typeof item.product_id === 'number' ? item.product_id : null,
          name: String(item.name || '').trim(),
          quantity: Number(item.quantity) || 0,
          price: Number(item.price) || 0,
          tax_rate: Number(item.tax_rate || item.taxRate || 18)
        };
      });

      const invoiceData = {
        partner_id: Number(invoice.partner_id) || null,
        invoice_number: String(invoice.invoiceNumber || '').trim(),
        date: String(invoice.date || ''),
        status: String(saveAsStatus), // Use the passed status parameter
        items: sanitizedItems.map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          tax_rate: item.tax_rate // Use tax_rate for backend consistency
        }))
      };

      console.log('Sending invoice data to backend:', invoiceData);

      const token = localStorage.getItem('token');
      console.log('Token found in localStorage:', token ? 'Yes' : 'No');
      console.log('Token length:', token ? token.length : 0);
      
      if (!token) {
        throw new Error(t('Ве молиме најавете се за да зачувате фактури.'));
      }

      let requestBody;
      try {
        requestBody = JSON.stringify(invoiceData);
      } catch (stringifyError) {
        console.error('Error serializing invoice data:', stringifyError);
        console.error('Problematic data:', invoiceData);
        throw new Error(t('Неуспешна подготовка на податоците за фактурата. Ве молиме проверете за невалидни податоци.'));
      }

      const url = isEdit && existingInvoiceData?.invoice?.id 
        ? `${apiUrl}/invoice/${existingInvoiceData.invoice.id}`
        : `${apiUrl}/invoice-add`;
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: requestBody,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Server error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Invoice saved successfully:', result);
      const invoiceId = result.id || existingInvoiceData?.invoice?.id;
      
      // Store the invoice ID for QR code generation
      if (!isEdit && result.id) {
        setSavedInvoiceId(result.id);
      }
      
      // Generate and upload PDF for complete invoices
      if (saveAsStatus === 'complete' && invoiceId) {
        try {
          console.log('Saving invoice PDF - invoiceId:', invoiceId, 'documentType: invoice');
          showAlert('info', t('Генерирање PDF'), t('Се креира PDF датотека за Вашата фактура...'));
          await handlePrintNote(invoice, showAlert, closeAlert, 'invoice', true, invoiceId);
        } catch (pdfError) {
          console.error('PDF generation failed:', pdfError);
          showAlert('warning', t('PDF предупредување'), t('Фактурата е успешно зачувана, но генерирањето PDF не успеа: {error}', { error: pdfError.message }));
        }
      }
      
      const isDraft = saveAsStatus === 'draft';
      const successVars = { number: invoiceData.invoice_number };
      let successMessage;
      if (isEdit) {
        successMessage = isDraft
          ? t('Фактура #{number} е успешно ажурирана како нацрт!', successVars)
          : t('Фактура #{number} е успешно ажурирана како завршена!', successVars);
      } else {
        successMessage = isDraft
          ? t('Фактура #{number} е успешно зачувана како нацрт!', successVars)
          : t('Фактура #{number} е успешно зачувана како завршена!', successVars);
      }
      const detailVars = { id: invoiceId, total: result.total };
      const successDetails = isEdit
        ? t('Системски ID: {id} • Вкупно: {total}', detailVars)
        : t('Системски ID: {id} • Вкупно: {total} • Формуларот е исчистен за следната фактура.', detailVars);

      showAlert('success', isEdit ? t('Фактурата е ажурирана!') : t('Фактурата е зачувана!'), successMessage, successDetails);
      
      // Clear validation errors on success
      setValidationErrors({});
      
      // Only reset form for new invoices, not when editing
      if (!isEdit) {
        await resetInvoiceForm();
      }
      
      // Optionally, you can redirect
      // navigate('/invoices'); // if you want to redirect to invoices list
      
    } catch (error) {
      console.error('Error saving invoice - Details:', {
        message: error.message,
        invoiceData: {
          invoiceNumber: invoice.invoiceNumber,
          recipient: invoice.recipient,
          itemsCount: invoice.items.length,
          validItemsCount: invoice.items.filter(item => item.name && item.quantity && item.price).length
        },
        apiUrl,
        error
      });
      
      let userMessage;
      
      if (error.message.includes('Failed to fetch')) {
        userMessage = t('Неуспешно зачувување на фактурата. Ве молиме проверете ја интернет врската и дали серверот работи.');
      } else if (error.message.includes('Server error: 500')) {
        userMessage = t('Неуспешно зачувување на фактурата. Настана грешка на серверот. Ве молиме обидете се повторно или контактирајте поддршка.');
      } else if (error.message.includes('required')) {
        userMessage = t('Неуспешно зачувување на фактурата. Некои задолжителни полиња недостасуваат или се невалидни.');
      } else {
        userMessage = t('Неуспешно зачувување на фактурата. {error}', { error: error.message });
      }
      
      showAlert('error', t('Неуспешно зачувување'), userMessage, error.message);
    }
  };

  const [IspratnicaEnabled, setIspratnicaEnabled] = useState(true);
  const [PodignalEnabled, setPodignalEnabled] = useState(true);
  const [voziloEnabled, setVoziloEnabled] = useState(true);
  const [reklamacijeEnabled, setReklamacijeEnabled] = useState(true);
  const [footerEnabled, setfooterEnabled] = useState(true);
  const [suggestionsPartner, setSuggestionsPartner] = useState([]);
  const [showPartnerSuggestions, setShowPartnerSuggestions] = useState(false);
  const [queryPartner, setQueryPartner] = useState('');

  const toggleIspratnicaEnabled = () => {
    setIspratnicaEnabled((prev) => {
      const newState = !prev;
      console.log('IspratnicaEnabled changed:', newState);
      return newState;
    });
  };
  const togglePodignalEnabled = () => setPodignalEnabled(!PodignalEnabled);
  const toggleVoziloEnabled = () => setVoziloEnabled(!voziloEnabled);
  const handleRemoveRow = (index) => {
    const updatedItems = invoice.items.filter((_, i) => i !== index);
    setInvoice((prev) => ({
      ...prev,
      items: updatedItems,
    }));
  };

  const handleAddRow = () => {
    const newItem = {
      id: Date.now(),
      name: '',
      quantity: 1,
      price: '',
      taxRate: 18, // Default tax rate
    };

    setInvoice((prev) => ({
      ...prev,
      items: [...prev.items, newItem],
    }));
  };

  const toggleReklamacije = () => {
    setReklamacijeEnabled((prev) => !prev);
  };

  const toggleFooter = () => {
    setfooterEnabled((prev) => !prev);
  };

  useEffect(() => {
    const fetchPartnersSuggestions = async () => {
      if (queryPartner.length < 1) {
        setSuggestionsPartner([]);
        return;
      }

      try {
        const res = await fetch(`${apiUrl}/partner-search?name=${encodeURIComponent(queryPartner)}`);
        if (res.ok) {
          const data = await res.json();
          setSuggestionsPartner(data);
        } else {
          console.error('Failed to fetch suggestions');
        }
      } catch (error) {
        console.error('Fetch error:', error);
      }
    };

    const timeout = setTimeout(fetchPartnersSuggestions, 100); // debounce

    return () => clearTimeout(timeout);
  }, [queryPartner]);

  const handleSelectPartner = (partner) => {
    setInvoice({
      ...invoice,
      partner_id: partner.id, // Store partner ID for backend
      recipient: partner.name,
      recipientAddress: partner.address,
      recipientBankNo: partner.bank_number,
    });
    setShowPartnerSuggestions(false);
  };
  //123456
  const handleSelectProductAtIndex = (idx, product) => {
    // Get the product information
    const updatedItems = [...invoice.items];
    
    // Update the item with product information, ensuring we properly set the tax rate
    updatedItems[idx] = {
      ...updatedItems[idx],
      product_id: product.id, // Store product ID for backend
      name: product.name,
      price: product.price || 0,
      taxRate: product.tax || 18, // Use tax field from database or default to 18%
      quantity: updatedItems[idx].quantity || 1, // preserve quantity or default to 1
    };

    // Update the invoice state
    setInvoice((prev) => ({
      ...prev,
      items: updatedItems,
    }));

    // Clear suggestions and active row
    setSuggestionsProduct([]);
    setProductActiveRow(null);
  };
  const [suggestionsProduct, setSuggestionsProduct] = useState([]);
  const [productActiveRow, setProductActiveRow] = useState(null); // track which row is focused

  const handleProductSearch = async (idx, value) => {
    setProductActiveRow(idx);
    handleProductItemChange(idx, 'name', value); // Update item.name

    try {
      const res = await fetch(`${apiUrl}/product-search?name=${encodeURIComponent(value)}`);
      if (!res.ok) throw new Error('Failed to fetch suggestions');

      const data = await res.json();
      setSuggestionsProduct(data); // Assuming array of product names
      setProductActiveRow(idx);
    } catch (error) {
      console.error('Error fetching product suggestions:', error);
      setSuggestionsProduct([]);
    }
  };

  useEffect(() => {
    const handleClickOutsideProduct = () => {
      setSuggestionsProduct([]);
      setProductActiveRow(null);
    };
    document.addEventListener('click', handleClickOutsideProduct);
    return () => document.removeEventListener('click', handleClickOutsideProduct);
  }, []);

  return (
    <div className="flex w-full">
      <div id="invoice" className="flex-1 w-[704px] min-h-[1023px] bg-white p-8 rounded shadow flex flex-col pt-0">
        {/* Header: Logo only - only show in advanced mode */}
        {viewMode === 'advanced' && (
          <div className="flex flex-col items-center mb-5 border-b border-gray-200 pb-2">
            <img src={invoice.logo} alt="Лого на компанијата" className="w-52 object-contain" />
            <p className="text-xs font-medium text-gray-700 -mt-2.5">
              {businessDetails.full_name || 'Назив на фирмата'}
            </p>
          </div>
        )}

        {/* Header - with added space on top only in basic mode */}
        <div id="header" className={`flex justify-between mb-6 ${viewMode === 'basic' ? 'mt-8' : 'mt-2'}`}>
          <div className="space-y-1 w-2/3 border border-gray-400 rounded p-2">
            <div className="flex items-start">
              <label className="text-sm font-medium w-10">До:</label>
              <div className="w-full">
                <input
                  type="text"
                  value={invoice.recipient}
                  onChange={(e) => {
                    setInvoice({ ...invoice, recipient: e.target.value });
                    setQueryPartner(e.target.value);
                    setShowPartnerSuggestions(true);
                    // Clear validation error when user starts typing
                    if (validationErrors.recipient) {
                      setValidationErrors(prev => ({ ...prev, recipient: undefined }));
                    }
                  }}
                  className={`w-full p-1 border rounded text-sm resize-none font-bold ${
                    validationErrors.recipient ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Примач"
                  rows={2}
                />

                {/* Partner dropdown suggestions */}
                {showPartnerSuggestions && suggestionsPartner.length > 0 && (
                  <ul className="absolute left-0 right-0 top-full mt-1 bg-white bg-blue-50 border border-gray-300 rounded shadow-md max-h-48 overflow-auto text-sm z-50">
                    {suggestionsPartner.map((partner) => (
                      <li
                        key={partner.id}
                        onClick={() => {
                          handleSelectPartner(partner);
                        }}
                        className="p-2 hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 hover:border-l-4 hover:border-blue-500 hover:shadow-sm cursor-pointer transition-all duration-200 ease-in-out"
                      >
                        <div className="font-medium">{partner.name}</div>
                        {partner.address && (
                          <div className="text-xs text-gray-600">{partner.address}</div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            {/* Always show recipient address and bank account fields in both modes */}
            <div className="flex items-start">
              <label className="text-sm font-medium w-10"></label>
              <input
                type="text"
                value={invoice.recipientAddress}
                onChange={(e) => setInvoice({ ...invoice, recipientAddress: e.target.value })}
                className="w-full p-1 border border-gray-300 rounded text-sm"
                placeholder="Адреса на примачот"
              />
            </div>
            <div className="flex items-center">
              <label className="text-sm font-medium w-10"></label>
              <input
                type="text"
                value={invoice.recipientBankNo}
                onChange={(e) => setInvoice({ ...invoice, recipientBankNo: e.target.value })}
                className="w-full p-1 border border-gray-300 rounded text-sm"
                placeholder="Банка - број на сметка"
              />
            </div>
          </div>

          {/* Right side with fixed labels aligned to the end */}
        <div className="space-y-2 w-fit text-right">
            {viewMode === 'advanced' && (
              <>
                <div className="grid grid-cols-2 gap-x-4">
                  <label className="text-sm">Жиро сметка</label>
                  <span className="text-sm">{businessDetails.bank_name} {businessDetails.bank_account }</span>
                </div>
                <div className="grid grid-cols-2 gap-x-4">
                  <label className="text-sm">ЕДБ</label>
                  <span className="text-sm">{businessDetails.tax_id }</span>
                </div>
              </>
            )}

            <div className={`grid grid-cols-2 items-center gap-x-4 `}>
              <label className="text-sm">Датум</label>
              <input
                type="date"
                value={invoice.date}
                onChange={(e) => setInvoice({ ...invoice, date: e.target.value })}
                className="text-sm text-gray-700 border border-gray-300 rounded"
              />
            </div>

            {viewMode === 'advanced' && (
              <div className="grid grid-cols-2 items-center gap-x-4">
                <label className="text-sm">Датум Валута</label>
                <input
                  type="date"
                  value={invoice.date}
                  onChange={(e) => setInvoice({ ...invoice, date: e.target.value })}
                  className="text-sm text-gray-700 border border-gray-300 rounded"
                />
              </div>
            )}
          </div>



        </div>

        {/* invoice number */}
        <div className="w-full flex justify-center items-center mt-4 mb-2">
          <div className="flex items-center space-x-1">
            <label htmlFor="invoiceNumber" className="text-lg text-gray-700 font-bold">
              Фактура #
            </label>
            <input
              type="text"
              id="invoiceNumber"
              name="invoiceNumber"
              className={`border px-2 py-1 rounded text-left w-24 font-bold -ml-0.5 print-hidden ${
                validationErrors.invoiceNumber ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="Фактура"
              value={invoice.invoiceNumber}
              onChange={(e) => {
                setInvoice({ ...invoice, invoiceNumber: e.target.value });
                // Clear validation error when user starts typing
                if (validationErrors.invoiceNumber) {
                  setValidationErrors(prev => ({ ...prev, invoiceNumber: undefined }));
                }
              }}
            />
          </div>
        </div>

        <table className="w-full mb-2 border-t1 border-b-2 border border-gray-400 rounded">
          <thead>
            <tr className="text-left border-b text-xs">
              <th className="py-0.5 w-6 text-center">#</th>
              <th className="py-0.5 w-2/5 whitespace-nowrap">Назив на артикал</th>
              <th className="py-0.5 w-8 text-center">EM</th>
              <th className="py-0.5 w-16 text-center">Количина</th>
              <th className="py-0.5 w-16 text-center">Цена</th>
              <th className="py-0.5 w-16 text-center">Без ДДВ</th>
              <th className="py-0.5 w-12 text-center">ДДВ %</th>
              <th className="py-0.5 w-20 text-right">ДДВ износ</th>
              <th className="py-0.5 w-16 text-right">Вкупно</th>
              <th className="py-0.5 w-5 text-right print:hidden"></th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((item, idx) => {
              const isItemValid = item.name?.trim() && 
                                  item.quantity && 
                                  item.price && 
                                  parseFloat(item.quantity) > 0 && 
                                  parseFloat(item.price) > 0;
              
              const hasAnyData = item.name?.trim() || item.quantity || item.price;
              const isIncompleteRow = hasAnyData && !isItemValid;
              
              const showItemError = validationErrors.items && (isIncompleteRow || !isItemValid);
              
              return (
                <tr key={item.id} className="border border-gray-300">
                  <td className="p-1 pl-2 text-xs">{idx + 1}</td>
                  {/* 123456 */}

                  <td className="p-1 relative">
                    <textarea
                      value={item.name}
                      placeholder="Назив на производ"
                      onChange={(e) => {
                        handleProductSearch(idx, e.target.value);
                        // Clear validation error when user starts typing
                        if (validationErrors.items) {
                          setValidationErrors(prev => ({ ...prev, items: undefined }));
                        }
                      }}
                      onFocus={() => setProductActiveRow(idx)}
                      className={`w-full px-1 py-0.5 text-xs resize-none leading-tight border-0 whitespace-pre-wrap break-words ${
                        showItemError ? 'bg-red-50' : ''
                      }`}
                      rows={3}
                      style={{ minHeight: '48px', lineHeight: '1.2' }}
                    />
                    {productActiveRow === idx && suggestionsProduct.length > 0 && (
                      <ul className="absolute left-0 z-10 w-full bg-white border border-gray-300 text-xs max-h-40 overflow-y-auto shadow-md">
                        {suggestionsProduct.map((suggestion, sidx) => (
                          <li
                            key={sidx}
                            className="px-2 py-1 cursor-pointer hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 hover:border-l-4 hover:border-blue-500 hover:shadow-sm transition-all duration-200 ease-in-out"
                            onClick={() => {
                              handleSelectProductAtIndex(idx, suggestion);
                              // handleProductItemChange(idx, 'name', suggestion.name);
                              setSuggestionsProduct([]);
                              setProductActiveRow(null);
                            }}
                          >
                            <div className="font-medium">{suggestion.name}</div>
                            {suggestion.price && (
                              <div className="text-gray-600">{t('Цена: {price}', { price: suggestion.price })}</div>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>

                  <td className="text-center">
                    <input type="text" value="пар" className="w-full px-1 py-0.5 text-xs text-center" />
                  </td>
                  <td>
                    <input
                      type="number"
                      placeholder="Количина"
                      min="1"
                      step="1"
                      value={Math.floor(item.quantity)}
                      onChange={(e) => {
                        const intValue = Math.floor(Number(e.target.value));
                        handleProductItemChange(idx, 'quantity', intValue || 1);
                        // Clear validation error when user starts typing
                        if (validationErrors.items) {
                          setValidationErrors(prev => ({ ...prev, items: undefined }));
                        }
                      }}
                      className={`w-full px-1 py-0.5 text-center placeholder:text-xs ${
                        showItemError ? 'bg-red-50' : ''
                      }`}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      placeholder="Цена"
                      value={item.price}
                      onChange={(e) => {
                        handleProductItemChange(idx, 'price', e.target.value);
                        // Clear validation error when user starts typing
                        if (validationErrors.items) {
                          setValidationErrors(prev => ({ ...prev, items: undefined }));
                        }
                      }}
                      className={`w-full px-1 py-0.5 text-center placeholder:text-xs ${
                        showItemError ? 'bg-red-50' : ''
                      }`}
                    />
                  </td>
                  <td className="px-1 py-0.5 text-center">{item.quantity && item.price ? (item.quantity * item.price).toFixed(2) : '-'}</td>
                  <td>
                    <select
                      value={item.taxRate}
                      onChange={(e) => handleProductItemChange(idx, 'taxRate', e.target.value)}
                      className="w-full px-1 py-0.5 border border-gray-300 rounded print-hidden text-center"
                    >
                      <option value="5">5%</option>
                      <option value="18">18%</option>
                      <option value="0">0%</option>
                    </select>
                    <span className="print-show hidden text-center">{item.taxRate || 18}%</span>
                  </td>
                  <td className="px-1 py-0.5 text-right">
                    {(() => {
                      const taxRate = parseFloat(item.taxRate || 18);
                      const quantity = parseFloat(item.quantity || 0);
                      const price = parseFloat(item.price || 0);
                      return quantity && price ? 
                        (quantity * price * (taxRate / 100)).toFixed(2) : '-';
                    })()}
                  </td>
                  <td className="px-1 py-0.5 text-right">
                    {(() => {
                      const taxRate = parseFloat(item.taxRate || 18);
                      const quantity = parseFloat(item.quantity || 0);
                      const price = parseFloat(item.price || 0);
                      const subtotal = quantity * price;
                      const taxAmount = subtotal * (taxRate / 100);
                      return quantity && price ? 
                        (subtotal + taxAmount).toFixed(2) : '-';
                    })()}
                  </td>
                  <td className="px-1 py-0.5 print-hidden">
                    <button
                      onClick={() => handleRemoveRow(idx)}
                      className="text-red-500 hover:text-red-700 text-sm p-0 m-0 w-4 h-4 leading-none"
                      title={t('Отстрани ред')}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Show validation errors */}
        {Object.keys(validationErrors).filter(key => validationErrors[key]).length > 0 && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
            <h4 className="text-sm font-medium text-red-800 mb-2">{t('Ве молиме поправете ги следниве грешки:')}</h4>
            <ul className="text-sm text-red-700 list-disc list-inside">
              {Object.entries(validationErrors)
                .filter(([_field, message]) => message) // Only show errors that still have messages
                .map(([field, message]) => (
                  <li key={field}>{message}</li>
                ))}
            </ul>
          </div>
        )}

        {/* Total Calculation */}
        <div id="calculation-1" className="flex justify-between mt-4 text-sm">
          {/* Left side: Additional Invoice Details - only show in advanced mode */}
          {viewMode === 'advanced' && (
            <div className="text-left space-y-1 text-sm">
              <div className="flex items-center gap-1">
                <span className="font-medium w-36">Начин на плаќање</span>
                <select className="border border-gray-300 rounded text-xs h-7 w-40 text-gray-700">
                  <option value="invoice">фактурно</option>
                  <option value="cash">профактурно</option>
                </select>
              </div>

              {IspratnicaEnabled && (
                <div className="flex items-center gap-1">
                  <span className="font-medium w-36">Испратница бр.</span>
                  <input type="text" value={invoice.dnote_number} className="border border-gray-300 rounded text-xs w-48 h-6 px-1" placeholder="#" />
                </div>
              )}

              {PodignalEnabled && (
                <div className="flex items-center gap-1">
                  <span className="font-medium w-36">Подигнал</span>
                  <input
                    type="text"
                    className="border border-gray-300 rounded text-xs w-48 h-6 px-1"
                    placeholder="Име"
                  />
                </div>
              )}

              {voziloEnabled && (
                <div className="flex items-center gap-1">
                  <span className="font-medium w-36">Возило</span>
                  <input type="text" className="border border-gray-300 rounded text-xs w-48 h-6 px-1" placeholder="#" />
                </div>
              )}
            </div>
          )}

          {/* Right side: Totals - always show in both modes */}
          <div className={`text-right space-y-1 ${viewMode === 'basic' ? 'ml-auto' : ''}`}>
            <div className="flex justify-start space-x-4">
              <div>
                Без ДДВ: <span className="font-medium">{subtotal.toFixed(2)}</span>
              </div>
              <div>
                ДДВ: <span className="font-medium">{tax.toFixed(2)}</span>
              </div>
            </div>
            
            {/* Tax breakdown by rate */}
            <div className="text-xs flex space-x-4 justify-end mt-1 text-gray-600">
              {[0, 5, 18].map(rate => {
                const itemsWithRate = invoice.items.filter(item => 
                  parseFloat(item.taxRate || 18) === rate && item.quantity && item.price
                );
                if (itemsWithRate.length === 0) return null;
                
                const taxBase = itemsWithRate.reduce((sum, item) => 
                  sum + parseFloat(item.quantity) * parseFloat(item.price), 0
                );
                const taxAmount = itemsWithRate.reduce((sum, item) => 
                  sum + parseFloat(item.quantity) * parseFloat(item.price) * (parseFloat(item.taxRate || 18) / 100), 0
                );
                
                return (
                  <div key={rate} className="flex space-x-2">
                    <span>{rate}%:</span> 
                    <span>база {taxBase.toFixed(2)},</span>
                    <span>ДДВ {taxAmount.toFixed(2)}</span>
                  </div>
                );
              })}
            </div>
            
            {/* <div>
              Попуст:{' '}
              <span className="font-medium text-red-600">
                -${discount.toFixed(2)}
              </span>
            </div> */}
            <div className="text-lg font-bold mt-2">
              Вкупно: <span className="text-blue-700">{total.toFixed(2)}</span>{' '}
              <span className="text-xs">{invoice.currency}</span>
            </div>
          </div>
        </div>


        
        {/* Reklamacije Note - controlled by reklamacijeEnabled toggle and only show in advanced mode */}
        {reklamacijeEnabled && viewMode === 'advanced' && (
          <div id="note-section" className="mt-4 p-4 border  bg-slate-200  bg-gray-400 border-gray-300 rounded shadow-md text-xs bg-gray-50">
            <div className="mb-2">
              <b>Напомена:</b>
            </div>
            <textarea
              value={invoice.reklamaciiText || ''}
              onChange={(e) => setInvoice({ ...invoice, reklamaciiText: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded text-xs bg-white resize-none"
              placeholder="Внесете текст за рекламации..."
              rows={3}
            />
          </div>
        )}

        {/* Footer - only show in advanced mode */}
        {viewMode === 'advanced' && (
          <div className="mt-auto print-footer">
            {/* --- SIGNATURES SECTION --- */}
            <div id="footer-1" className="flex w-full justify-between items-end mb-4">
                {/* Left Signature Block */}
                <div className="w-1/2">
                    <div className="w-2/3 ml-10">
                        <span className="text-[12px] text-gray-600">__.__.2025</span>
                        <div className="border-t border-gray-400 mt-1 mb-1"></div>
                        <span className="text-[10px] text-gray-600 w-full block text-center">Примил</span>
                    </div>
                </div>
                {/* Right Signature Block */}
                <div className="w-1/2">
                    <div className="w-2/3 ml-auto mr-10 text-center">
                        <div className="border-t border-gray-400 w-full mb-1"></div>
                        <span className="text-[10px] text-gray-600">Издал</span>
                    </div>
                </div>
            </div>

            {/* --- BUSINESS DETAILS SECTION --- */}
            {footerEnabled && (
                <div id="footer-2" className="w-full relative text-xs text-gray-700 leading-tight mt-2" style={{ minHeight: '64px' }}>

                    {/* Address (Left) - This element remains in the normal flow */}
                    <div className="text-left">
                        <p>{`${businessDetails.address}, ${businessDetails.city}`}</p>
                         <p>
                            <a href={businessDetails.website || 'http://www.elkos.mk'} target="_blank" rel="noopener noreferrer">
                                {businessDetails.website || 'www.elkos.mk'}
                            </a> | 
                            email: <a href={`mailto:${businessDetails.email || 'info@elkos.mk'}`}>
                                {businessDetails.email || 'info@elkos.mk'}
                            </a>
                        </p>

                        <p>тел: {businessDetails.phone || '071562449'}</p>
                    </div>

                    {/* QR Code (Right) - This is taken out of the flow and pinned to the right */}
                    <div className="absolute right-0 top-0">
                        <QRCodeGenerator
                            data={(() => {
                              const invoiceId = (isEdit && existingInvoiceData?.invoice?.id) || savedInvoiceId;
                              const companyId = "1"; // Default company ID - could be made dynamic later
                              
                              if (invoiceId) {
                                // New URL structure with company/folder/filename
                                const cleanFilename = `Фактура-${invoice.invoiceNumber}_${new Date().toISOString()}.pdf`;
                                return `https://pulse-iota-seven.vercel.app/preview/${companyId}/invoices/${encodeURIComponent(cleanFilename)}`;
                              } else {
                                // Fallback to old structure
                                return `https://pulse-iota-seven.vercel.app/preview/${encodeURIComponent(`invoice-${invoice.invoiceNumber}.pdf`)}`;
                              }
                            })()}
                            size={64}
                            className="w-16 h-16 object-contain"
                        />
                    </div>

                </div>
            )}
          </div>
        )}
      </div>

      {/* Sidebar - only show if hideSidebar is false */}
      {!hideSidebar && (
        <div className="w-48 p-4 bg-gray-50 border-l flex flex-col gap-2 print:hidden">
        {/* <button
          onClick={handlePreview}
          className="w-full bg-white border border-gray-300 text-gray-800 py-2 rounded hover:bg-gray-100"
        >
          Preview
        </button> */}
                {/* <button
          onClick={handlePreview}
          className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700"
        >
          Preview
        </button> */}
        {/* <button
          onClick={handlePrintClick}
          className="w-full bg-white border border-gray-300 text-gray-800 py-2 rounded hover:bg-gray-100"
        >
          Print
        </button>
        <button
          onClick={() => handleSaveDraft()}
          className="w-full bg-yellow-100 text-yellow-900 py-2 rounded hover:bg-yellow-200"
        >
          Save Draft
        </button>
        <button onClick={() => handleSave()} className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700">
          Save
        </button> */}

        {/* <button
          onClick={handleSaveAndPrint}
          className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700"
        >
          Save and Print
        </button> */}
        {/* Divider line */}
        <hr className="my-4 border-t border-gray-300" />

        {/* Settings section */}
        <div>
          <div className="text-sm font-semibold text-gray-600 mb-2">{t('Подесувања')}</div>
          <div className="flex flex-col gap-2">
            <button
              onClick={handleAddRow}
              className="w-full bg-gray-200 text-gray-800 text-sm py-1 rounded hover:bg-gray-300"
            >
              {t('+ Додади ред')}
            </button>
            <button
              onClick={toggleReklamacije}
              className={`w-full text-sm py-1 rounded bg-white text-gray-800 hover:bg-gray-100 border ${
                reklamacijeEnabled ? 'border-green-600' : 'border-red-300'
              }`}
            >
              {reklamacijeEnabled ? t('Рекламации вкл.') : t('Рекламации искл.')}
            </button>

            <button
              onClick={toggleFooter}
              className={`w-full text-sm py-1 rounded bg-white text-gray-800 hover:bg-gray-100 border ${
                footerEnabled ? 'border-green-600' : 'border-red-300'
              }`}
            >
              {footerEnabled ? t('Подножје вкл.') : t('Подножје искл.')}
            </button>

            <div className="space-y-2 mt-2">
              {/* Ispratnica Enabled Checkbox */}
              <div className="text-xs font-semibold text-gray-600 mb-2">{t('Прикажи полиња')}</div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={IspratnicaEnabled}
                  onChange={toggleIspratnicaEnabled}
                  className={`form-checkbox h-4 w-4 text-green-600 border ${
                    IspratnicaEnabled ? 'border-green-600' : 'border-red-300'
                  }`}
                />
                <label className="ml-2 text-sm text-gray-800">{t('Испратница бр.')}</label>
              </div>

              {/* Podignal Enabled Checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={PodignalEnabled}
                  onChange={togglePodignalEnabled}
                  className={`form-checkbox h-4 w-4 text-green-600 border ${
                    PodignalEnabled ? 'border-green-600' : 'border-red-300'
                  }`}
                />
                <label className="ml-2 text-sm text-gray-800">{t('Подигнал')}</label>
              </div>

              {/* Vozilo Enabled Checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={voziloEnabled}
                  onChange={toggleVoziloEnabled}
                  className={`form-checkbox h-4 w-4 text-green-600 border ${
                    voziloEnabled ? 'border-green-600' : 'border-red-300'
                  }`}
                />
                <label className="ml-2 text-sm text-gray-800">{t('Возило')}</label>
              </div>
            </div>
          </div>
        </div>
        </div>
      )}
      
      {/* Custom Alert Modal */}
      <CustomAlert
        isOpen={alert.isOpen}
        onClose={closeAlert}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        details={alert.details}
      />
    </div>
  );
});

InvoiceGenerator.displayName = 'InvoiceGenerator';

export default InvoiceGenerator;
